import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';

// 8-bit pixel art for each category using CSS box-shadow sprites
// Each string row = one pixel row, chars map to colors
const PX = 4;
const COLORS = {
  p: '#9147ff', P: '#e4f2c1', r: '#eb0400', R: '#ff6b6b', g: '#4caf50', G: '#81c784',
  b: '#2196f3', B: '#64b5f6', o: '#ff9800', O: '#ffcc80', y: '#ffd700', Y: '#fff176',
  k: '#ff6b9d', K: '#ff9dbf', c: '#00bcd4', C: '#4dd0e1', w: '#efeff1', d: '#2a2a2e',
  m: '#ce93d8', M: '#e1bee7', f: '#ff5722', F: '#ff8a65', n: '#ffa726', N: '#ffcc80',
  l: '#66bb6a', L: '#a5d6a7', s: '#adadb8', x: '#f44336', X: '#ef9a9a',
};

function PixelArt({ rows, size = PX }) {
  const shadows = [];
  const parsed = rows.map(r => r.split(''));
  for (let y = 0; y < parsed.length; y++) {
    for (let x = 0; x < parsed[y].length; x++) {
      const ch = parsed[y][x];
      if (ch !== '.' && COLORS[ch]) {
        shadows.push(`${x * size}px ${y * size}px 0 ${COLORS[ch]}`);
      }
    }
  }
  const w = Math.max(...parsed.map(r => r.length));
  return (
    <div style={{
      width: w * size, height: parsed.length * size,
      position: 'relative', margin: '0 auto',
    }}>
      <div style={{
        position: 'absolute', width: size, height: size,
        boxShadow: shadows.join(','),
      }} />
    </div>
  );
}

// ---- Pixel art sprites (12x12 ish) ----

const SPRITES = {
  gaming: [
    '............',
    '..dddddddd..',
    '.dppppppppd.',
    '.dpPPPPPPpd.',
    '.dpPPPPPPpd.',
    '.dppppppppd.',
    '.dddddddddd.',
    '..d..dd..d..',
    '.ddd.dd.ddd.',
    '.dPd.dd.dPd.',
    '.ddd.dd.ddd.',
    '............',
  ],
  hangout: [
    '............',
    '..kkkkkkkk..',
    '.kKKKKKKKKk.',
    '.kKddKddKKk.',
    '.kKKKKKKKKk.',
    '.kKKdddKKKk.',
    '.kKKKKKKKKk.',
    '..kkkkkkkk..',
    '....kk......',
    '...kk.......',
    '............',
    '............',
  ],
  music: [
    '........pp..',
    '.......pppp.',
    '.......p..p.',
    '.......p..p.',
    '.......p..p.',
    '.......p..p.',
    '..pp...p..p.',
    '.pPPp.pp.pp.',
    'pPPPPppPPPp.',
    'pPPPPppPPPp.',
    '.pPPp..pPp..',
    '..pp....pp..',
  ],
  art: [
    '...oooo.....',
    '..oOOOOo....',
    '.oOrrgbo....',
    'oOrrrgbo....',
    'oOOOOgbo....',
    'oOOOObbo....',
    '.oOOOoo.....',
    '..oooo......',
    '.....ss.....',
    '......ss....',
    '.......ss...',
    '........s...',
  ],
  craft: [
    '............',
    '..ss........',
    '.s..s.......',
    's....s.oooo.',
    '.s..s.oOOOo.',
    '..ss.oOOOOo.',
    '.s..soOOOOo.',
    's....oOOOOo.',
    '.s..s.oOOOo.',
    '..ss...oooo.',
    '............',
    '............',
  ],
  coding: [
    '............',
    '.dddddddddd.',
    '.dggggggggd.',
    '.dg.gg...gd.',
    '.dg..g.g.gd.',
    '.dg.g..g.gd.',
    '.dg....g.gd.',
    '.dgGGGGGGd.',
    '.dggggggggd.',
    '.dddddddddd.',
    '............',
    '............',
  ],
  esports: [
    '....yyyy....',
    '...yYYYy....',
    '..yYYYYYy...',
    '.xxYYYYYxx..',
    'xxxyYYYyxxx.',
    'xXx.yYy.xXx',
    'xXx..y..xXx',
    '.xx.....xx..',
    '..xxxxxxx...',
    '..xXXXXXx...',
    '..xxxxxxx...',
    '............',
  ],
  fitness: [
    '............',
    '....ww......',
    '...wdw......',
    '..llwll.....',
    '.l.lwl.l....',
    '...lwl......',
    '..ll.ll.....',
    '.ll...ll....',
    '.l.....l....',
    '............',
    '............',
    '............',
  ],
  education: [
    '............',
    '..bbbbbbbb..',
    '.bBBBBBBBBb.',
    '.bBssBsBBBb.',
    '.bBBBBBBBBb.',
    '.bBsBBssBBb.',
    '.bBBBBBBBBb.',
    '.bBssBsBBBb.',
    '.bBBBBBBBBb.',
    '..bbbbbbbb..',
    '....b..b....',
    '............',
  ],
  focus: [
    '............',
    '...kkkkkk...',
    '..kKKKKKKk..',
    '.kKkkkkkKKk.',
    'kKkk..kkKKk.',
    'kKk.kk.kKKk.',
    'kKk.kk.kKKk.',
    'kKkk..kkKKk.',
    '.kKkkkkkKKk.',
    '..kKKKKKKk..',
    '...kkkkkk...',
    '............',
  ],
  anime: [
    '............',
    '....www.....',
    '...wdddw....',
    '..wdddddw...',
    '.wdYddYddw..',
    '.wddddddw...',
    '.wdd.d.dw...',
    '.wdddddw....',
    '..wwdww.....',
    '...www......',
    '..w...w.....',
    '............',
  ],
};

const CATEGORY_COLORS = {
  hangout:         '#ff6b9d',
  gaming:          '#9147ff',
  esports:         '#f44336',
  anime:           '#e040fb',
  music:           '#e91e63',
  art:             '#ff9800',
  craft:           '#e89140',
  coding:          '#4caf50',
  education:       '#42a5f5',
  focus:           '#ff6b9d',
  fitness:         '#66bb6a',
};

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.categories()
      .then(setCategories)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Show all 12 categories even if DB doesn't have them yet
  const allCats = Object.keys(CATEGORY_COLORS);
  const catData = allCats.map(name => {
    const fromApi = categories.find(c => c.category === name);
    return {
      category: name,
      live_count: fromApi?.live_count || 0,
      viewer_count: fromApi?.viewer_count || 0,
      channel_count: fromApi?.channel_count || 0,
    };
  });

  return (
    <div className="page">
      <h1 style={{ fontFamily: 'var(--font-body)', fontSize: '1.6rem', fontWeight: 900, marginBottom: '8px' }}>
        Browse Categories
      </h1>
      <p style={{ color: '#adadb8', marginBottom: '24px' }}>Discover streams by category</p>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#adadb8' }}>Loading...</div>
      ) : (
        <div className="category-grid">
          {catData.map(cat => {
            const color = CATEGORY_COLORS[cat.category] || '#D6E9AA';
            const sprite = SPRITES[cat.category];
            return (
              <div
                key={cat.category}
                className="category-card"
                onClick={() => navigate(`/?category=${encodeURIComponent(cat.category)}`)}
                style={{ '--cat-color': color }}
              >
                {/* Pixel art sprite */}
                <div className="category-sprite">
                  {sprite ? <PixelArt rows={sprite} size={PX} /> : (
                    <div style={{ fontFamily: 'var(--font-pixel)', fontSize: '1.5rem', color }}>?</div>
                  )}
                </div>

                {/* Label */}
                <div className="category-label" style={{ color }}>
                  {cat.category}
                </div>

                {/* Stats */}
                <div className="category-stats">
                  {cat.live_count > 0 ? (
                    <span className="category-live">{cat.live_count} LIVE</span>
                  ) : (
                    <span>offline</span>
                  )}
                  {cat.viewer_count > 0 && <span>{cat.viewer_count.toLocaleString()} viewers</span>}
                </div>
                <div className="category-channels">
                  {cat.channel_count} channel{cat.channel_count !== 1 ? 's' : ''}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
